import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchMe, logout as logoutApi, type AuthUser } from '@/application/auth/api'
import { BACKEND_ENABLED } from '@/infrastructure/config/featureFlags'
import { AuthContext, type AuthState } from './AuthContext'

/**
 * Tracks the current auth session. On mount, calls /v1/me to discover
 * whether the cookie is valid. The offline review surface does NOT
 * depend on this state — auth is opt-in (PRD #63 / #64).
 *
 * 401 responses are not exceptional: they just transition the state
 * back to anonymous. Phase 4 Should bullet ("graceful 401 handling
 * without disrupting offline review") falls out for free here —
 * neither LibraryRoute nor ProjectRoute consult AuthState.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // With the backend disabled there is no /v1/me to poll, so we skip
  // the 'loading' state entirely — the provider starts (and stays)
  // anonymous. That keeps the effect below a no-op and avoids the
  // react-hooks/set-state-in-effect lint rule.
  const [state, setState] = useState<AuthState>(() =>
    BACKEND_ENABLED ? { status: 'loading' } : { status: 'anonymous' },
  )

  const refresh = useCallback(async () => {
    if (!BACKEND_ENABLED) return
    const result = await fetchMe()
    if (result.ok) {
      setState({ status: 'authenticated', user: result.user })
    } else {
      setState({ status: 'anonymous' })
    }
  }, [])

  useEffect(() => {
    if (!BACKEND_ENABLED) return
    let cancelled = false
    void fetchMe().then((result) => {
      if (cancelled) return
      setState(
        result.ok
          ? { status: 'authenticated', user: result.user }
          : { status: 'anonymous' },
      )
    })
    return () => {
      cancelled = true
    }
  }, [])

  const setUser = useCallback((user: AuthUser) => {
    setState({ status: 'authenticated', user })
  }, [])

  const signOut = useCallback(async () => {
    if (BACKEND_ENABLED) await logoutApi()
    setState({ status: 'anonymous' })
  }, [])

  const value = useMemo(
    () => ({ state, setUser, signOut, refresh }),
    [state, setUser, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
